import { useEffect, useState } from "react";
import { ActivityItem } from "@/lib/webContentTypes";
import {
  createAdminActivities,
  deleteAdminActivities,
  getAdminActivities,
  updateAdminActivities,
} from "@/lib/webContentApi";

interface Props {
  onAuthExpired: () => void;
}

const emptyForm: Omit<ActivityItem, "id" | "kind"> = {
  title: "",
  summary: "",
  content: "",
  date: new Date().toISOString().split("T")[0],
  location: "",
  imageUrl: "",
  active: true,
};

export default function AdminActivities({ onAuthExpired }: Props) {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ActivityItem | null>(null);
  const [form, setForm] = useState<Omit<ActivityItem, "id" | "kind">>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setItems(await getAdminActivities());
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : "No se pudo cargar actividades";
      if (message.toLowerCase().includes("sesion") || message.toLowerCase().includes("token")) {
        onAuthExpired();
        return;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...form, content: form.content || form.summary };
      if (editing) {
        await updateAdminActivities(editing.id, payload);
      } else {
        await createAdminActivities(payload);
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      await load();
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : "No se pudo guardar";
      if (message.toLowerCase().includes("sesion") || message.toLowerCase().includes("token")) {
        onAuthExpired();
        return;
      }
      setError(message);
    }
  };

  const handleEdit = (item: ActivityItem) => {
    setEditing(item);
    setForm({
      title: item.title,
      summary: item.summary,
      content: item.content,
      date: item.date,
      location: item.location,
      imageUrl: item.imageUrl,
      active: item.active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Eliminar esta actividad?")) return;
    try {
      await deleteAdminActivities(id);
      await load();
    } catch (deleteError) {
      const message = deleteError instanceof Error ? deleteError.message : "No se pudo eliminar";
      if (message.toLowerCase().includes("sesion") || message.toLowerCase().includes("token")) {
        onAuthExpired();
        return;
      }
      setError(message);
    }
  };

  const toggleActive = async (item: ActivityItem) => {
    try {
      await updateAdminActivities(item.id, { active: !item.active });
      await load();
    } catch (toggleError) {
      const message = toggleError instanceof Error ? toggleError.message : "No se pudo actualizar";
      if (message.toLowerCase().includes("sesion") || message.toLowerCase().includes("token")) {
        onAuthExpired();
        return;
      }
      setError(message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-sky-900">Actividades ({items.length})</h2>
        <button
          onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm); }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm cursor-pointer transition-colors"
        >
          <i className="ri-add-line"></i> Nueva actividad
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-sky-900">{editing ? "Editar actividad" : "Nueva actividad"}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Titulo *</label>
                <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Fecha</label>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Lugar</label>
                  <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">URL de imagen</label>
                <input type="url" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} placeholder="https://..." className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Descripcion *</label>
                <textarea required rows={4} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value, content: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-sky-400 resize-none" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="active-act" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 cursor-pointer" />
                <label htmlFor="active-act" className="text-sm font-semibold text-slate-700 cursor-pointer">Publicar (visible en el sitio)</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm cursor-pointer transition-colors">
                  {editing ? "Guardar cambios" : "Crear actividad"}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm cursor-pointer hover:bg-slate-50 transition-colors">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Cargando actividades...</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className={`flex items-center gap-4 p-4 rounded-xl border bg-white ${item.active ? "border-slate-100" : "border-slate-100 opacity-60"}`}>
              {item.imageUrl && (
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover object-top" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-xs text-slate-400">{new Date(item.date).toLocaleDateString("es-CL")}</span>
                  {!item.active && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Oculto</span>}
                </div>
                <h4 className="font-semibold text-slate-800 text-sm truncate">{item.title}</h4>
                <p className="text-xs text-slate-400 truncate"><i className="ri-map-pin-line mr-1"></i>{item.location}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleActive(item)} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${item.active ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}>
                  <i className={item.active ? "ri-eye-line" : "ri-eye-off-line"}></i>
                </button>
                <button onClick={() => handleEdit(item)} className="w-8 h-8 flex items-center justify-center rounded-lg text-sky-600 hover:bg-sky-50 cursor-pointer transition-colors">
                  <i className="ri-edit-line"></i>
                </button>
                <button onClick={() => handleDelete(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 cursor-pointer transition-colors">
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
